import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaPalette, FaMusic, FaTheaterMasks, FaFilm, FaMoneyBillWave, FaUsers } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import styled, { keyframes } from "styled-components";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(50px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  margin-bottom: 30px;
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
  height: 100%;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const ServiceIcon = styled.div`
  font-size: 42px;
  color: ${props => props.color || "#0d6efd"};
  margin-bottom: 20px;
`;

const ServiceTitle = styled.h4`
  font-weight: 700;
  margin-bottom: 15px;
  color: #333;
`;

const ServiceDescription = styled.p`
  color: #666;
  font-size: 16px;
  line-height: 1.6;
`;

const Section = styled.section`
  padding: 80px 0;
  background: ${props => props.background || "white"};
`;

const ServiceListItem = styled.div`
  display: flex;
  margin-bottom: 15px;
  opacity: 0;
  transform: translateY(30px);
  animation: ${fadeUp} 0.6s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
`;

const ServiceListIcon = styled.div`
  margin-right: 15px;
  color: #0d6efd;
  flex-shrink: 0;
  margin-top: 3px;
`;

const ServiceListContent = styled.div`
  flex-grow: 1;
`;

const ServiceListTitle = styled.h5`
  font-weight: 700;
  margin-bottom: 5px;
  color: #333;
`;

const ServiceListDescription = styled.p`
  color: #666;
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 0;
`;

const ArtCultureDivertissement = () => {
    const [setIsVisible] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const faqSection = document.getElementById("faq-section");
            if (faqSection) {
                const rect = faqSection.getBoundingClientRect();
                if (rect.top < window.innerHeight * 0.8 && rect.bottom > 0) {
                    setIsVisible(true);
                } else {
                    setIsVisible(false);
                }
            }
        };

        window.addEventListener("scroll", handleScroll);
        handleScroll();
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const servicesData = [
        {
            icon: <FaMoneyBillWave />,
            title: "Comptabilité, Fiscalité et Paie",
            description: "Gestion comptable adaptée, fiscalité spécifique et gestion de la paie pour tous les acteurs de l'art et de la culture.",
            color: "#0d6efd"
        },
        {
            icon: <FaFilm />,
            title: "Gestion et Pilotage",
            description: "Comptabilité analytique, tableaux de bord, budgets prévisionnels et gestion de trésorerie adaptés aux projets artistiques.",
            color: "#198754"
        },
        {
            icon: <FaTheaterMasks />,
            title: "Conseil et Accompagnement Stratégique",
            description: "Accompagnement à la création, développement et optimisation de vos projets artistiques et culturels.",
            color: "#dc3545"
        },
        {
            icon: <FaMusic />,
            title: "Formations",
            description: "Formations sur-mesure pour les professionnels de l'art, de la culture et du divertissement.",
            color: "#6610f2"
        }
    ];

    const serviceDetails = {
        comptabilite: [
            {
                title: "Tenue et révision comptable",
                description: "Gestion rigoureuse de votre comptabilité, adaptée à votre statut (artiste-auteur, intermittent du spectacle, association, société, etc.) et à votre activité (production, diffusion, édition, etc.)."
            },
            {
                title: "Établissement des comptes annuels",
                description: "Bilan, compte de résultat, annexe, et production de liasses fiscales."
            },
            {
                title: "Gestion de la TVA",
                description: "Maîtrise des différents régimes de TVA applicables (droits d'auteur, billetterie, prestations de services, etc.), déclarations et télétransmissions."
            },
            {
                title: "Optimisation fiscale",
                description: "Recherche des meilleures options pour réduire votre charge fiscale (crédits d'impôt, exonérations, régimes spécifiques, etc.). Gestion des particularités fiscales (droits d'auteur, revenus artistiques, etc.)."
            },
            {
                title: "Déclarations fiscales et sociales",
                description: "Prise en charge de l'ensemble de vos déclarations."
            },
            {
                title: "Gestion de la paie et du social",
                description: "Établissement des bulletins de paie, déclarations sociales, gestion des contrats de travail (intermittents, permanents, etc.). Maîtrise des conventions collectives applicables."
            },
            {
                title: "Assistance en cas de contrôle fiscal ou social",
                description: "Accompagnement complet lors des contrôles administratifs."
            },
            {
                title: "Gestion des droits d'auteur et droits voisins",
                description: "Déclaration, calcul, répartition des droits liés à votre activité créative."
            }
        ],
        gestion: [
            {
                title: "Comptabilité analytique",
                description: "Mise en place d'outils de suivi des coûts par projet, par spectacle, par production, etc. Analyse de la rentabilité."
            },
            {
                title: "Tableaux de bord et indicateurs clés de performance (KPI)",
                description: "Suivi en temps réel de vos performances (billetterie, taux de remplissage, coûts de production, etc.)."
            },
            {
                title: "Budgets prévisionnels",
                description: "Élaboration de budgets réalistes et suivi budgétaire rigoureux (par projet, par saison, etc.)."
            },
            {
                title: "Analyse financière et diagnostic",
                description: "Identification des forces, faiblesses, opportunités et menaces (SWOT) de votre structure."
            },
            {
                title: "Gestion de trésorerie",
                description: "Prévisions, optimisation des flux, recherche de solutions de financement à court terme."
            }
        ],
        conseil: [
            {
                title: "Accompagnement à la création et au développement",
                description: "Étude de faisabilité, business plan, choix de la structure juridique (association, société, entreprise individuelle, etc.), recherche de financements (subventions, mécénat, crowdfunding, etc.), formalités administratives."
            },
            {
                title: "Conseil en financement",
                description: "Optimisation de votre structure financière, recherche de financements (prêts bancaires, etc.)."
            },
            {
                title: "Évaluation de projets artistiques et culturels",
                description: "Analyse de la viabilité économique, recherche de partenaires."
            },
            {
                title: "Conseil en gestion des ressources humaines",
                description: "Recrutement, fidélisation, gestion des contrats, optimisation des coûts salariaux."
            },
            {
                title: "Conseil juridique",
                description: "(En partenariat avec des avocats spécialisés) : contrats d'artistes, contrats de production, contrats de diffusion, propriété intellectuelle, etc."
            },
            {
                title: "Accompagnement à la transformation digitale",
                description: "Mise en place d'outils numériques de gestion, de communication, de billetterie, etc."
            }
        ],
        formation: [
            {
                title: "Formations sur mesure",
                description: "Adaptées aux besoins spécifiques de votre structure culturelle ou artistique."
            },
            {
                title: "Formations inter- ou intra-structures",
                description: "Pour les professionnels de l'art et de la culture."
            }
        ]
    };

    return (
        <>
            <Header />

            <section className="service-banner d-flex align-items-center">
                <div className="container-fluid px-4 px-md-5 text-white">
                    <div className="row">
                        <div className="col-lg-12">
                            <h1 className="fw-bold display-4 mb-3">Expertise Comptable et Conseil pour les Acteurs de l'Art, de la Culture et du Divertissement</h1>
                            <p className="fs-5 mb-4">Créateurs, Producteurs, Diffuseurs : Nous Soutenons Votre Talent et Vos Projets</p>
                            <nav aria-label="breadcrumb">

                            </nav>
                        </div>
                    </div>
                </div>
            </section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row">
                        <div className="col-12">
                            <h2 className="fw-bold custom-title text-center mb-5">
                                CINEF-EC comprend les spécificités du monde artistique et culturel
                            </h2>
                            <p className="text-muted text-center fs-5 mb-5">
                                Le monde de l'art, de la culture et du divertissement est un univers passionnant, mais aussi complexe sur le plan administratif, comptable et fiscal. 
                                Que vous soyez artiste, compagnie, association culturelle, producteur de spectacles, galerie d'art, organisateur d'événements, 
                                ou entreprise du secteur des industries créatives, CINEF-EC vous offre un accompagnement sur mesure. 
                                Nous comprenons vos spécificités et vous aidons à gérer votre activité en toute sérénité, 
                                pour que vous puissiez vous consacrer pleinement à votre créativité.
                            </p>
                        </div>
                    </div>

                    <div className="row align-items-center">
                        <div className="col-lg-12">
                            <h3 className="fw-bold custom-title mb-4">Une expertise dédiée aux professionnels de l'art et de la culture</h3>
                            <p className="mb-4">
                                Notre cabinet dispose d'une expertise particulière dans l'accompagnement des acteurs du monde artistique et culturel.
                                Nous connaissons parfaitement les spécificités comptables, fiscales et juridiques liées à ce secteur.
                            </p>
                            <p className="mb-4">
                                Notre objectif est de vous accompagner dans vos obligations légales et réglementaires
                                tout en vous aidant à développer vos projets et à optimiser leur gestion.
                            </p>
                            <div className="d-flex mt-4">
                                <div className="d-flex align-items-center me-4">
                                    <div className="rounded-circle bg-primary d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                        <FaPalette className="text-white fs-4" />
                                    </div>
                                    <span className="ms-2 fw-bold">Artistes</span>
                                </div>
                                <div className="d-flex align-items-center me-4">
                                    <div className="rounded-circle bg-success d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                        <FaTheaterMasks className="text-white fs-4" />
                                    </div>
                                    <span className="ms-2 fw-bold">Compagnies</span>
                                </div>
                                <div className="d-flex align-items-center">
                                    <div className="rounded-circle bg-danger d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                        <FaMusic className="text-white fs-4" />
                                    </div>
                                    <span className="ms-2 fw-bold">Producteurs</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">1. Comptabilité, Fiscalité et Paie</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.comptabilite.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`comptabilite-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>

                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">2. Gestion et Pilotage</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.gestion.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`gestion-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>

                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">3. Conseil et Accompagnement Stratégique</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.conseil.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`conseil-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>

                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">4. Formations</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.formation.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`formation-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row justify-content-center mb-5">
                        <div className="col-12 col-md-10 col-lg-8">
                            <div className="call-to-action p-5 rounded-3 text-center">
                                <h3 className="fw-bold text-white mb-3">Vous recherchez un partenaire expert pour vous accompagner dans la gestion de votre activité artistique ou culturelle ?</h3>
                                <p className="text-white fs-5 mb-4">
                                    Contactez-nous dès aujourd'hui pour une consultation gratuite et personnalisée.
                                    Nous évaluerons ensemble vos besoins et vous proposerons des solutions adaptées à votre projet et à vos ambitions.
                                </p>
                                <a href="/contact" className="btn btn-light btn-lg fw-bold px-4">Demander un entretien gratuit</a>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            <Footer />

            <style>{`
        .service-banner {
          background: linear-gradient(to right, rgba(0, 138, 68, 0.85), rgba(220, 53, 69, 0.85)), 
                      url("/assets/images/arts-culture-banner.jpg");
          background-size: cover;
          background-position: center;
          height: 400px;
          display: flex;
          align-items: center;
        }

        .breadcrumb {
          background: transparent;
          font-size: 18px;
        }

        .breadcrumb-item a {
          color: white;
          font-weight: 500;
        }

        .custom-title {
          position: relative;
          padding-bottom: 15px;
        }

        .custom-title:after {
          content: '';
          position: absolute;
          left: 0;
          bottom: 0;
          width: 60px;
          height: 3px;
          background:  linear-gradient(to right, #008a44, #dc3545);
        }

        .text-center .custom-title:after {
          left: 50%;
          transform: translateX(-50%);
        }

        .custom-underline {
          height: 4px;
          width: 70px;
          background:  linear-gradient(to right, #008a44, #dc3545);
          margin-top: 10px;
        }

        .call-to-action {
          background:  linear-gradient(to right, #008a44, #dc3545);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        #faq-section {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s ease;
        }

        #faq-section.visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        @media (max-width: 768px) {
          .service-banner {
            height: 350px;
          }
          
          .custom-title {
            font-size: 1.75rem;
          }
          
          .d-flex {
            flex-wrap: wrap;
          }
          
          .d-flex .d-flex {
            margin-bottom: 10px;
          }
        }

        .fa-check-circle {
          color: #0d6efd;
        }
      `}</style>
        </>
    );
};

export default ArtCultureDivertissement;